// Which Vee bumper the start page shows (docs/BUMPERS.md). Pure: no DOM, no storage. The bumper
// chunk, Settings, the tests and Ep3 (a copy, pinned by test vectors) all pick with it.
//
// A list entry has the manifest's fields (public/bumpers/manifest.json): id, weight, season
// (a key of SEASONS, or null), dates ({ from, until } New York days, until not included, or null),
// bg ("any" | "dark" | "light") and, for moving art, animated + still.
import { SEASONS, seasonOpen, nyDay } from "./seasons.js";

export const MODES = ["visit", "day", "pinned", "off"];
// How often a visit picks from the open season's set rather than the everyday set. A fixed share,
// not weight math, so new everyday art never waters the season down.
export const SEASON_SHARE = 0.7;

// "YYYY-MM-DD" in New York for an instant.
export function nyDateKey(now) {
	const p = new Intl.DateTimeFormat("en-CA", { timeZone: "America/New_York", year: "numeric", month: "2-digit", day: "2-digit" }).formatToParts(new Date(now));
	const v = (t) => p.find((x) => x.type === t).value;
	return v("year") + "-" + v("month") + "-" + v("day");
}

// Whether an entry may show at `now` on a `look` ("dark" | "light"). `lite` (Lite mode, reduced
// motion or animations off) needs a still for moving art.
export function showable(b, now, look = "dark", lite = false) {
	if (!b || !(b.weight > 0)) return false;
	if (b.season && !seasonOpen(b.season, now)) return false;
	if (b.dates && !(now >= nyDay(b.dates.from) && now < nyDay(b.dates.until))) return false;
	if (b.bg && b.bg !== "any" && b.bg !== look) return false;
	if (lite && b.animated && !b.still) return false;
	return true;
}

export const eligible = (list, now, look, lite) => (list || []).filter((b) => showable(b, now, look, lite));

// FNV-1a, then mulberry32: the same numbers for the same day and list everywhere.
function seeded(str) {
	let h = 0x811c9dc5;
	for (let i = 0; i < str.length; i++) { h ^= str.charCodeAt(i); h = Math.imul(h, 0x01000193); }
	let a = h >>> 0;
	return () => {
		a = (a + 0x6d2b79f5) | 0;
		let t = Math.imul(a ^ (a >>> 15), 1 | a);
		t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
		return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
	};
}

function weighted(pool, r) {
	let total = 0;
	for (const b of pool) total += b.weight;
	let x = r() * total;
	for (const b of pool) { x -= b.weight; if (x < 0) return b; }
	return pool[pool.length - 1];
}

// The pool a draw comes from: a special day's art only, else the open season's set most of the
// time, else the everyday set.
function draw(list, r, avoid) {
	if (!list.length) return null;
	let pool = list.filter((b) => b.dates);
	if (!pool.length) {
		const season = list.filter((b) => b.season), everyday = list.filter((b) => !b.season);
		pool = season.length && (!everyday.length || r() < SEASON_SHARE) ? season : everyday;
	}
	if (avoid && pool.length > 1) pool = pool.filter((b) => b.id !== avoid);
	return weighted(pool, r);
}

// One a day: the same bumper all day in New York, for this list.
export function dayPick(list, now, look = "dark", lite = false) {
	const el = eligible(list, now, look, lite);
	return draw(el, seeded(nyDateKey(now) + ":" + el.map((b) => b.id).join(",")), null);
}

// opts: mode (MODES), now, last (the id shown last time, never repeated on "visit" while there is
// another), pin (the pinned id), look, lite, rnd.
export function pick(list, { mode = "visit", now = Date.now(), last = "", pin = "", look = "dark", lite = false, rnd = Math.random } = {}) {
	if (mode === "off") return null;
	if (mode === "pinned") {
		// A pin shows only when it could show anyway: a winter one waits for winter (Settings says
		// so, see pinWaiting) and a retired one is gone. Until then, a normal visit pick.
		const p = (list || []).find((b) => b.id === pin);
		if (showable(p, now, look, lite)) return p;
	}
	if (mode === "day") return dayPick(list, now, look, lite);
	return draw(eligible(list, now, look, lite), rnd, last);
}

// Why a pinned bumper isn't showing at `now`: { season, label, from, over } while its season is
// closed, { day, from, over } while its special day hasn't come or has gone. null when the pin can
// show, or when it's missing or retired (nothing to wait for).
export function pinWaiting(list, pin, now = Date.now()) {
	const p = pin && (list || []).find((b) => b.id === pin);
	if (!p || !(p.weight > 0)) return null;
	const s = p.season && SEASONS[p.season];
	if (s && !seasonOpen(p.season, now)) return { season: p.season, label: s.label, from: s.from, over: now >= s.until };
	if (p.dates) {
		const from = nyDay(p.dates.from), until = nyDay(p.dates.until);
		if (!(now >= from && now < until)) return { day: p.dates.from, from, over: now >= until };
	}
	return null;
}
